import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useDragLayer } from 'react-dnd';
import { Ribbon, theme, Typo } from '../../../../ui';
import { useCourse, useCourseToc } from '../../../course/hooks';
import TocMenuSection from './TocMenuSection';
import { shadeColor } from '../../../../ui/theme/colors';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '../../../settings/hooks';
import { useCurrentCourseId, useCurrentPageId, useCurrentSectionId, useCourseSections } from '../../hooks';
import { ItemTypes } from '../../../editCourse/utils/dragTypes';
import { useReorderSectionsMutation } from '../../../editCourse/editCourseApi';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS } from '../../../../utils/constants';
import { DraggableSection } from './components';

// Component that uses useDragLayer - only render within DndProvider context
const DraggableSectionWrapper = ({ section, index, onDrop, themeColor, children }) => {
  const { isDraggingSection } = useDragLayer((monitor) => ({
    isDraggingSection: monitor.isDragging() && monitor.getItemType() === ItemTypes.SECTION,
  }));

  return (
    <DraggableSection section={section} index={index} onDrop={onDrop} themeColor={themeColor}>
      {React.cloneElement(children, { isSectionDragging: isDraggingSection })}
    </DraggableSection>
  );
};

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: contain;
`;

const HeadWrapper = styled.div`
  background: ${({ $ultralightColor }) => $ultralightColor || theme.colors.NEU_100};
  margin: 0;
  max-width: 600px;
`;

const CourseName = styled.div`
  min-height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  p {
    padding: 5px 5px 5px 15px;
  }
`;

const SectionsWrapper = styled.div`
  flex: 1;
  min-height: 100px;
`;

const TocMenu = ({ isExpanded = true, isStructureEditMode = false }) => {
  const { t } = useTranslation();
  const course = useCourse();
  const toc = useCourseToc();
  const activePageId = useCurrentPageId();
  const courseId = useCurrentCourseId();
  const currentSectionId = useCurrentSectionId();
  const refActivePage = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const hasMountedRef = useRef(false);

  const features = useFeatures();
  const courseSections = useCourseSections();
  const [reorderSections] = useReorderSectionsMutation();

  // Drag-and-drop handler for sections
  const handleSectionDrop = useCallback(
    async (draggedSectionId, targetIndex) => {
      try {
        const sections = [...courseSections].sort((a, b) => a.order - b.order);
        const currentIndex = sections.findIndex((s) => s.id === draggedSectionId);

        if (currentIndex === targetIndex) return; // No change

        // Reorder sections
        const reorderedSections = [...sections];
        const [removed] = reorderedSections.splice(currentIndex, 1);

        // Adjust target index when dragging down
        const adjustedTargetIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
        reorderedSections.splice(adjustedTargetIndex, 0, removed);

        const sectionIds = reorderedSections.map((s) => s.id);
        await reorderSections({ courseId, sectionIds }).unwrap();
        enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      } catch (error) {
        console.error('Failed to reorder sections:', error);
        enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      }
    },
    [courseSections, reorderSections, courseId, t],
  );

  // Scroll to active page when it changes or when panel expands
  useEffect(() => {
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Scroll if we have an active page ref
    // This triggers on: page change, panel expand, or initial mount
    if (refActivePage.current) {
      // Use setTimeout to ensure accordion expansion animation is complete
      scrollTimeoutRef.current = setTimeout(() => {
        if (refActivePage.current) {
          refActivePage.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center', // Center the active page in the viewport
            inline: 'nearest',
          });
        }
      }, 150); // Delay for accordion animation
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activePageId, isExpanded]);

  // Ensure scroll happens on initial mount (e.g., page refresh)
  useEffect(() => {
    if (!hasMountedRef.current && refActivePage.current && activePageId) {
      hasMountedRef.current = true;

      // Longer delay on initial mount to ensure everything is rendered
      scrollTimeoutRef.current = setTimeout(() => {
        if (refActivePage.current) {
          refActivePage.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 300);
    }
  }, [toc, activePageId]);

  const ultralightColor = shadeColor(course?.theme?.color, 0.95);

  return (
    <>
      {course && (
        <HeadWrapper $ultralightColor={ultralightColor}>
          {features.draftCourses && <Ribbon word={course.draft ? t('globals.draft') : ''} />}
          {course.coverImage?.url && <StyledImage src={course.coverImage?.url} alt='Course' />}
          <CourseName>
            <Typo.MediumText fontWeight='600' $textAlign='left'>
              {course.name}
            </Typo.MediumText>
            <Typo.SmallText fontWeight='300' $textAlign='left'>
              {t('globals.by', { name: course.ownerName })}
            </Typo.SmallText>
          </CourseName>
        </HeadWrapper>
      )}

      <SectionsWrapper>
        {toc?.map((section, i) => {
          const isActive = currentSectionId === section.id;
          if (!isStructureEditMode && !section.pages?.length) {
            return null;
          }

          // Wrap with DraggableSectionWrapper when in edit mode (includes drag layer detection)
          if (isStructureEditMode) {
            return (
              <DraggableSectionWrapper key={section.id} section={section} index={i} onDrop={handleSectionDrop} themeColor={course?.theme?.color}>
                <TocMenuSection
                  themeColor={course?.theme?.color}
                  section={section}
                  activePageId={activePageId}
                  active={isActive}
                  courseId={courseId}
                  activePageRef={refActivePage}
                  isStructureEditMode={isStructureEditMode}
                  isExpanded={isExpanded}
                  sectionIndex={i}
                  totalSections={toc?.length || 0}
                />
              </DraggableSectionWrapper>
            );
          }

          // View mode - no drag layer context needed
          return (
            <TocMenuSection
              key={section.id}
              themeColor={course?.theme?.color}
              section={section}
              activePageId={activePageId}
              active={isActive}
              courseId={courseId}
              activePageRef={refActivePage}
              isStructureEditMode={isStructureEditMode}
              isExpanded={isExpanded}
              sectionIndex={i}
              totalSections={toc?.length || 0}
              isSectionDragging={false}
            />
          );
        })}
      </SectionsWrapper>
    </>
  );
};

export default TocMenu;
